<template>
	<Container
		:gap="12"
		:padding="0"
		overflow="visible"
		align="left center"
		class="flow-avatar-uploader"
		@click.native="handleClick"
		@drop.native.prevent="dropFile($event)"
		@dragover.native.prevent="() => {}"
	>
		<input ref="fileInput" :accept="accept" type="file" @input="selectFile" />
		<Pictogram
			v-if="avatar.type === 'label'"
			state="avatar"
			:label="avatar.label"
			size="medium"
			shape="circle"
		></Pictogram>
		<Pictogram v-if="avatar.type === 'icon'" size="medium" shape="circle">
			<Icon :name="avatar.icon" type="filled" :effects="false" />
		</Pictogram>
		<Pictogram v-if="avatar.type === 'file'" size="medium" shape="circle" type="transparent">
			<img :src="avatar.src" />
		</Pictogram>
		<Button
			v-if="avatar.type === 'file'"
			type="error"
			state="icon"
			size="small"
			class="avatar-remove-btn"
			@click.prevent.stop="removeFile()"
		>
			<Icon name="i-close" type="filled" :effects="false" size="xx-small" />
		</Button>
		<Container type="block" direction="column" :gap="0" :padding="0" align="left center">
			<Button type="default" size="small" class="flow-upload-btn" @click.prevent="() => {}">{{
				buttonText
			}}</Button>
			<Typography type="p3" color="light" class="flow-avatar-uploader-subtitle">
				{{ info }}
			</Typography>
		</Container>
	</Container>
</template>
<script lang="ts">
import Container from "../Container/Container.vue";
import Pictogram from "../Pictogram/Pictogram.vue";
import Button from "../Button/Button.vue";
import Typography from "../Typography/Typography.vue";
import Icon from "../Icon/Icon.vue";
import { defineComponent, PropType } from "vue";

export default defineComponent({
	name: "AvatarUpload",

	components: {
		Container,
		Pictogram,
		Button,
		Typography,
		Icon
	},

	props: {
		/**
		 * file type or size info can be displayed using this prop
		 */
		info: {
			type: String,
			default: () => "Note: Max size 1 MB • Supported files PNG, JPG"
		},

		/**
		 * text to display on button
		 */
		buttonText: {
			type: String,
			default: () => "Upload"
		},

		/**
		 * accepted file types
		 */
		accept: {
			type: String,
			default: () => "image/*"
		},

		/**
		 * file to display while editing
		 */
		file: {
			type: File as PropType<File>,
			default: () => null
		},

		/**
		 * placeholder icon name
		 */
		placeholderIconName: {
			type: String,
			default: () => "i-user"
		},

		/**
		 * label to display when there is no file selected.
		 */
		label: {
			type: String as PropType<string>,
			default: () => null
		}
	},

	data: () => ({
		innerFile: null as any
	}),

	computed: {
		avatar() {
			if (this.innerFile) {
				return {
					type: "file",
					src: URL.createObjectURL(this.innerFile)
				} as const;
			} else if (this.file) {
				return {
					type: "file",
					src: URL.createObjectURL(this.file)
				} as const;
			} else if (this.label) {
				return {
					type: "label",
					label: this.label
				} as const;
			} else if (this.placeholderIconName) {
				return {
					type: "icon",
					icon: this.placeholderIconName
				} as const;
			}

			return {
				type: "icon",
				icon: "i-user"
			} as const;
		}
	},

	methods: {
		handleClick() {
			(this.$refs.fileInput as InstanceType<typeof HTMLInputElement>).click();
		},

		dropFile(e: DragEvent) {
			this.innerFile = Array.from(e.dataTransfer ? e.dataTransfer.files : [])[0];
			/**
			 * Whenever File is selected.
			 *
			 * @event onFileSelect
			 */
			this.$emit("onFileSelect", this.innerFile);
			/**
			 * Whenever Files are  changed.
			 *
			 * @event change
			 */
			this.$emit("change", this.innerFile);
			/**
			 * input event for validation
			 *
			 * @event input
			 */
			this.$emit("input", this.innerFile);
		},

		selectFile() {
			const fileInputRef = this.$refs.fileInput as InstanceType<typeof HTMLInputElement>;
			this.innerFile = Array.from(fileInputRef.files ? fileInputRef.files : [])[0];
			this.$emit("onFileSelect", this.innerFile);
			this.$emit("change", this.innerFile);
			this.$emit("input", this.innerFile);
		},

		removeFile() {
			this.innerFile = null;
			this.$emit("onFileSelect", null);
			this.$emit("change", null);
			this.$emit("input", null);
		}
	}
});
</script>
<style lang="scss">
div.flow-avatar-uploader {
	position: relative;
	> input[type="file"] {
		display: none;
	}

	.flow-avatar-uploader-subtitle {
		padding-left: 4px;
	}

	div.container[data-type="block"] > * + * {
		margin-top: 6px;
	}
	button.avatar-remove-btn.button-base[state="icon"] {
		width: 16px;
		height: 16px;

		position: absolute;

		top: 34px;
		left: 34px;
	}
}
</style>
